import { ThumbsDown, ThumbsUp } from 'lucide-react-native';
import { GestureResponderEvent } from 'react-native';

import { Pressable } from '@/components/ui/pressable';
import { View } from '@/components/ui/view';
import { ANALYTICS_EVENTS } from '@/constants/analytics';
import { LIKE_STATUSES } from '@/constants/contentLibrary';
import { useAbilityGate } from '@/hooks/useAbilityGate';
import { useColors } from '@/hooks/useColors';
import { type LikeStatus } from '@/types/contentLibraryItems';
import { getAccessibilityProps } from '@/util/accessibilityProps';
import { segmentClient } from '@/util/analytics';

export function ContentLibraryItemFeedback({
  changeLikeStatus,
  likeStatus,
}: {
  changeLikeStatus: (newLikeStatus: LikeStatus) => void;
  likeStatus: LikeStatus;
}) {
  const { guard } = useAbilityGate();

  const { colors } = useColors();

  const isLiked = likeStatus === LIKE_STATUSES.liked;

  const isNotLiked = likeStatus === LIKE_STATUSES.notLiked;

  const updateLikeStatus = async (newLikeStatus: LikeStatus, oldLikeStatus: LikeStatus) => {
    try {
      changeLikeStatus(newLikeStatus);

      segmentClient.track(ANALYTICS_EVENTS.CONTENT_LIBRARY_ITEM_LIKE_STATUS_CHANGED, {
        likeStatus: newLikeStatus,
      });
    } catch (error) {
      console.error('updateLikeStatus error', error);

      changeLikeStatus(oldLikeStatus);
    }
  };

  return (
    <View className="flex flex-row items-center pb-8">
      <Pressable
        className="w-auto self-start py-5 pr-5"
        {...getAccessibilityProps({ label: 'Helpful', role: 'button' })}
        aria-pressed={isLiked}
        accessibilityState={{ selected: isLiked }}
        onPress={async (event: GestureResponderEvent) => {
          event.preventDefault();

          event.stopPropagation();

          if (guard('update', 'ContentLibraryItem')) {
            let newLikeStatus: LikeStatus;

            if (isLiked) {
              newLikeStatus = null;
            } else {
              newLikeStatus = LIKE_STATUSES.liked;
            }

            await updateLikeStatus(newLikeStatus, likeStatus);
          }
        }}>
        <ThumbsUp
          color={colors.primary[400]}
          fill={isLiked ? colors.primary[400] : 'transparent'}
        />
      </Pressable>
      <Pressable
        className="w-auto self-start p-5"
        {...getAccessibilityProps({ label: 'Not helpful', role: 'button' })}
        aria-pressed={isNotLiked}
        accessibilityState={{ selected: isNotLiked }}
        onPress={async (event: GestureResponderEvent) => {
          event.preventDefault();

          event.stopPropagation();

          if (guard('update', 'ContentLibraryItem')) {
            let newLikeStatus: LikeStatus;

            if (isNotLiked) {
              newLikeStatus = null;
            } else {
              newLikeStatus = LIKE_STATUSES.notLiked;
            }

            await updateLikeStatus(newLikeStatus, likeStatus);
          }
        }}>
        <ThumbsDown
          color={colors.primary[400]}
          fill={isNotLiked ? colors.primary[400] : 'transparent'}
        />
      </Pressable>
    </View>
  );
}
